/**
 * @fileoverview Syndicator Showdown — Player Leaderboards Page
 * @module pages/LeaderboardsPage
 *
 * Multi-tab player-facing leaderboard: Showdowns (per-game rankings),
 * ELO Rankings (global skill rating), and Stats (records/achievements),
 * plus a "Your Ranking" snapshot sidebar.
 *
 * Data sources (real field names — verified against the deployed Cloud
 * Functions, not assumed):
 *   - game_slates/{slateId}/leaderboard/{userId} — public read, written by
 *     settleRaceResult. Fields: displayName, totalEarnings, racesSettled,
 *     position, userId.
 *   - player_profiles/{uid} — public read. Mirrors eloRating/eloPeak/
 *     eloGamesPlayed/eloLastDelta from the private users/{uid} doc (see
 *     calculateEloRatings in showdown-functions/index.js) so the ELO tab
 *     can list every player, not just the signed-in one.
 *   - users/{uid}/elo_history/{id} — owner-only read. Fields: eloBefore,
 *     eloAfter, eloDelta, rank, gameName, calculatedAt.
 *   - player_entries/{slateId}_{uid} + its race_allocations subcollection —
 *     owner-only read. Used for the current user's own "Best Race" figure
 *     and expandable race-by-race breakdown.
 *
 * Fields the backend does not track yet (gamesWon, top10Finishes,
 * longestWinStreak, currentStreak, bestFinish, a global "highest single
 * game score" record) render as "Coming soon" rather than fabricating data.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, query, where, orderBy, limit,
  getDocs, getDoc, doc, onSnapshot,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { getEntryEarnings } from '../utils/stableStatus';
import { getPublicName, getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import { getRaceProgress } from '../utils/showdownStatus';
import {
  getDefaultLeaderboardSlate, getLeaderboardStatusLabel, sortLeaderboardSelectorOptions,
  buildEnteredSlateIdSet,
} from '../utils/leaderboardSelection';
import { LeaderboardDetail } from '../components/leaderboard/LeaderboardDetail';
import './LeaderboardsPage.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString('en-AU')}`;
}

function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Math.round(n).toLocaleString('en-AU');
}

function rankIcon(rank) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return null;
}

function rankClass(rank) {
  if (rank === 1) return 'lb-rank--1';
  if (rank === 2) return 'lb-rank--2';
  if (rank === 3) return 'lb-rank--3';
  return '';
}

function eloTier(rating) {
  if (rating === null || rating === undefined) return null;
  if (rating >= 1900) return { key: 'champion', label: 'Champion' };
  if (rating >= 1600) return { key: 'elite',    label: 'Elite' };
  if (rating >= 1300) return { key: 'sharp',    label: 'Sharp' };
  if (rating >= 1000) return { key: 'proven',   label: 'Proven' };
  return { key: 'rookie', label: 'Rookie' };
}

function movementDirection(delta) {
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'flat';
}

// ─── Shared bits ─────────────────────────────────────────────────────────────

function TableSkeleton({ rows = 5 }) {
  return (
    <div className="lb-skeleton-wrap">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="lb-skeleton" />
      ))}
    </div>
  );
}

function EmptyState({ icon, message }) {
  return (
    <div className="lb-empty-state">
      <div className="lb-empty-state__icon">{icon}</div>
      <p>{message}</p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="lb-error-state">
      <p>{message || 'Something went wrong loading the leaderboard. Please try again.'}</p>
      <button className="lb-retry-btn" onClick={onRetry} type="button">Retry</button>
    </div>
  );
}

// ─── Your Ranking sidebar (desktop card + mobile compact strip) ─────────────

function YourRankingSidebar({ profile, tier, totalEarnings, rank, compact = false }) {
  const rows = [
    { key: 'rank',  label: 'Current Rank',    value: rank != null ? `#${rank}` : '—' },
    { key: 'elo',   label: 'Current ELO',     value: fmtNum(profile?.eloRating) },
    { key: 'tier',  label: 'ELO Tier',        value: tier?.label ?? '—', tierKey: tier?.key },
    { key: 'peak',  label: 'Peak ELO',        value: fmtNum(profile?.eloPeak) },
    { key: 'games', label: 'Games Played',    value: profile?.eloGamesPlayed != null ? fmtNum(profile.eloGamesPlayed) : '—' },
    { key: 'won',   label: 'Games Won',       value: '—' },
    { key: 'top10', label: 'Top 10 Finishes', value: '—' },
    { key: 'earn',  label: 'Total Earnings',  value: fmtMoney(totalEarnings) },
    { key: 'best',  label: 'Best Finish',     value: '—' },
    { key: 'streak',label: 'Current Streak',  value: '—' },
  ];

  const renderValue = (r) => (
    r.tierKey
      ? <span className={`lb-tier-badge lb-tier-badge--${r.tierKey}`}>{r.value}</span>
      : r.value
  );

  if (compact) {
    const pillKeys = ['rank', 'elo', 'tier', 'earn'];
    return (
      <>
        {rows.filter(r => pillKeys.includes(r.key)).map(r => (
          <div key={r.key} className="lb-mobile-stat-pill">
            <span className="lb-mobile-stat-pill__label">{r.label}</span>
            <span className="lb-mobile-stat-pill__value">{renderValue(r)}</span>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="lb-sidebar">
      <div className="lb-sidebar__title">Your Ranking</div>
      {rows.map(r => (
        <div key={r.key} className="lb-sidebar__stat">
          <span className="lb-sidebar__label">{r.label}</span>
          <span className="lb-sidebar__value">{renderValue(r)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Showdowns tab ────────────────────────────────────────────────────────────

/**
 * Game selector option label — game name + relevance status badge, plus a
 * race-progress fragment ("3/9 resulted") for anything not yet completed so
 * the dropdown makes it obvious which games are live/completed/upcoming.
 */
function selectorOptionLabel(slate) {
  const statusLabel = getLeaderboardStatusLabel(slate);
  const { totalRaces, resultedRaces } = getRaceProgress(slate);
  const progressFragment = statusLabel !== 'COMPLETED' && totalRaces > 0
    ? ` — ${resultedRaces}/${totalRaces} resulted`
    : '';
  return `${slate.gameName || slate.id} — ${statusLabel}${progressFragment}`;
}

function ShowdownsTab({ currentUser, selectedSlateId, onSelectSlate }) {
  const [slates, setSlates] = useState([]);
  const [slatesLoading, setSlatesLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  // True once the user has manually chosen a game from the dropdown — from
  // then on we never override their choice, even if currentUser resolves
  // (login state) or changes after that point.
  const userPickedRef = React.useRef(false);
  // Tracks which "identity" (uid, or 'anon' while signed out) the default
  // has already been resolved for, so a later currentUser resolution (e.g.
  // auth settling just after this tab mounts) gets one chance to correct an
  // initial anonymous-tier guess into the user's actual relevant game.
  const defaultResolvedForRef = React.useRef(undefined);

  // Game selector — last 30 games, most recent first. No `status` filter:
  // game_slates.status is only ever 'building' | 'live' | 'finalised' in
  // this codebase, never 'active' — filtering on that value silently hid
  // every currently-live game from this list. 'building' (not yet
  // published to players) is excluded client-side below.
  useEffect(() => {
    setSlatesLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'game_slates'), orderBy('gameCloseTime', 'desc'), limit(30)),
      snap => {
        setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.status !== 'building'));
        setSlatesLoading(false);
      },
      e => {
        console.error('[ShowdownsTab] slate list error:', e.code, e.message, e);
        setSlatesLoading(false);
      }
    );
    return unsub;
  }, []);

  // This user's own entries — used both to resolve the default game and to
  // order the selector by relevance. Only a real submitted/resulted entry
  // counts (see buildEnteredSlateIdSet), never an unsubmitted draft.
  useEffect(() => {
    if (!currentUser) { setEntries([]); setEntriesLoaded(true); return undefined; }
    setEntriesLoaded(false);
    const unsub = onSnapshot(
      query(collection(db, 'player_entries'), where('userId', '==', currentUser.uid)),
      snap => {
        setEntries(snap.docs.map(d => d.data()));
        setEntriesLoaded(true);
      },
      e => {
        console.error('[ShowdownsTab] entries load error:', e.code, e.message, e);
        setEntriesLoaded(true);
      }
    );
    return unsub;
  }, [currentUser]);

  const enteredSlateIds = useMemo(() => buildEnteredSlateIdSet(entries), [entries]);

  // Default game selection — see utils/leaderboardSelection.js
  // getDefaultLeaderboardSlate() for the full priority order. Re-runs if
  // `currentUser`/entries resolve after the slate list has already loaded
  // (e.g. auth settling a beat later), but only while the user hasn't
  // manually picked a game themselves.
  useEffect(() => {
    if (slatesLoading || slates.length === 0 || !entriesLoaded || userPickedRef.current) return;
    const identity = currentUser?.uid || 'anon';
    if (defaultResolvedForRef.current === identity) return;
    defaultResolvedForRef.current = identity;
    const defaultId = getDefaultLeaderboardSlate({ userEntries: entries, slates });
    if (defaultId) onSelectSlate(defaultId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slates, slatesLoading, entries, entriesLoaded, currentUser]);

  // Ordered by relevance: in-play user-entered games with results first,
  // then completed user-entered, then user-entered upcoming, then public
  // in-play games with results, then public completed. See
  // utils/leaderboardSelection.js sortLeaderboardSelectorOptions().
  const orderedSlates = useMemo(
    () => sortLeaderboardSelectorOptions(slates, enteredSlateIds),
    [slates, enteredSlateIds]
  );

  return (
    <div>
      <div className="lb-filters">
        <select
          className="lb-select"
          value={selectedSlateId || ''}
          onChange={e => { userPickedRef.current = true; onSelectSlate(e.target.value); }}
          disabled={slatesLoading || orderedSlates.length === 0}
        >
          {orderedSlates.map(s => (
            <option key={s.id} value={s.id}>{selectorOptionLabel(s)}</option>
          ))}
        </select>
      </div>

      {slatesLoading ? (
        <div className="lb-card"><TableSkeleton /></div>
      ) : orderedSlates.length === 0 ? (
        <div className="lb-card"><EmptyState icon="🏇" message="No leaderboards available yet — enter a game to start competing!" /></div>
      ) : !selectedSlateId ? (
        <div className="lb-card"><EmptyState icon="🏇" message="Pick a game above to see its leaderboard." /></div>
      ) : (
        <LeaderboardDetail key={selectedSlateId} slateId={selectedSlateId} />
      )}
    </div>
  );
}

// ─── ELO tab ─────────────────────────────────────────────────────────────────

function EloRow({ row, rank, isSelf, isExpanded, canExpand, onToggleExpand, history }) {
  const tier = eloTier(row.eloRating);
  const dir = movementDirection(row.eloLastDelta || 0);
  return (
    <React.Fragment>
      <tr
        className={`${canExpand ? 'lb-row--clickable ' : ''}${isSelf ? 'lb-row--self' : ''}`}
        onClick={canExpand ? onToggleExpand : undefined}
      >
        <td className={rankClass(rank)}>{rank ? (rankIcon(rank) || rank) : 'Outside Top 50'}</td>
        <td>
          <span className="lb-player-cell">
            <img
              className="lb-player-avatar"
              src={getProfileImageUrl(row, null)}
              alt=""
              onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
            />
            <span className="lb-player-cell__name">{getPublicName(row, null)}</span>
          </span>
        </td>
        <td style={{ color: '#fff', fontWeight: 700 }}>{fmtNum(row.eloRating)}</td>
        <td className="lb-col--tier">
          {tier && <span className={`lb-tier-badge lb-tier-badge--${tier.key}`}>{tier.label}</span>}
        </td>
        <td className="lb-col--peak lb-muted">{fmtNum(row.eloPeak)}</td>
        <td className="lb-col--games">{fmtNum(row.eloGamesPlayed)}</td>
        <td className="lb-col--winrate lb-muted">—</td>
        <td>
          {row.eloLastDelta ? (
            <span className={`lb-movement--${dir}`}>
              {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '—'} {Math.abs(row.eloLastDelta)}
            </span>
          ) : <span className="lb-muted">—</span>}
        </td>
      </tr>
      {isExpanded && (
        <tr className="lb-expand-row">
          <td colSpan={7}>
            <div className="lb-expand-panel">
              {!history ? (
                <div className="lb-expand-empty">Loading…</div>
              ) : history.length === 0 ? (
                <div className="lb-expand-empty">No Elo history yet.</div>
              ) : (
                history.map((h, i) => (
                  <div key={i} className="lb-expand-line">
                    <span className="lb-expand-line__label">{h.gameName || h.gameId}</span>
                    <span>{fmtNum(h.eloBefore)} → {fmtNum(h.eloAfter)}</span>
                    <span className={`lb-expand-line__value ${h.eloDelta >= 0 ? 'lb-expand-line--pos' : 'lb-expand-line--neg'}`}>
                      {h.eloDelta >= 0 ? '+' : ''}{h.eloDelta} {h.eloDelta > 0 ? '▲' : h.eloDelta < 0 ? '▼' : ''}
                    </span>
                  </div>
                ))
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function EloTab({ currentUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [expandedUid, setExpandedUid] = useState(null);
  const [historyByUid, setHistoryByUid] = useState({});
  const [ownRow, setOwnRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(query(
          collection(db, 'player_profiles'),
          where('eloGamesPlayed', '>', 0),
          orderBy('eloRating', 'desc'),
          limit(50)
        ));
        if (!cancelled) setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('[EloTab] rankings load error:', e.code, e.message, e);
        // failed-precondition means the composite index is missing or still
        // building — that's "no Elo data to show yet", not a real error.
        // Anything else (permission-denied, unavailable, etc.) is a genuine
        // failure and should surface the retryable error state.
        if (!cancelled) {
          if (e.code === 'failed-precondition') {
            setRows([]);
          } else {
            setError('Something went wrong loading ELO rankings. Please try again.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [retryKey]);

  // Pin the current user's row if their ranking falls outside the top 50.
  useEffect(() => {
    if (!currentUser) { setOwnRow(null); return undefined; }
    if (rows.some(r => r.id === currentUser.uid)) { setOwnRow(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const profSnap = await getDoc(doc(db, 'player_profiles', currentUser.uid));
        if (cancelled) return;
        if (profSnap.exists() && profSnap.data().eloGamesPlayed) {
          setOwnRow({ id: currentUser.uid, ...profSnap.data() });
          return;
        }
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!cancelled) {
          setOwnRow(userSnap.exists() && userSnap.data().eloGamesPlayed
            ? { id: currentUser.uid, ...userSnap.data() }
            : null);
        }
      } catch (e) {
        console.error('[EloTab] own row fallback error:', e.code, e.message, e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, rows]);

  const handleToggleExpand = useCallback(async (uid) => {
    setExpandedUid(prev => (prev === uid ? null : uid));
    if (!currentUser || uid !== currentUser.uid) return; // privacy — self only
    if (historyByUid[uid]) return;
    try {
      const snap = await getDocs(query(
        collection(db, 'users', uid, 'elo_history'),
        orderBy('calculatedAt', 'desc'),
        limit(10)
      ));
      setHistoryByUid(prev => ({ ...prev, [uid]: snap.docs.map(d => d.data()) }));
    } catch (e) {
      console.error('[EloTab] elo_history load error:', e.code, e.message, e);
      setHistoryByUid(prev => ({ ...prev, [uid]: [] }));
    }
  }, [currentUser, historyByUid]);

  if (loading) return <div className="lb-card"><TableSkeleton /></div>;
  if (error) return <div className="lb-card"><ErrorState message={error} onRetry={() => setRetryKey(k => k + 1)} /></div>;
  if (rows.length === 0 && !ownRow) {
    return <div className="lb-card"><EmptyState icon="📊" message="ELO rankings appear after your first completed showdown." /></div>;
  }

  return (
    <div className="lb-card">
      <div className="lb-table-scroll">
        <table className="lb-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>ELO</th>
              <th className="lb-col--tier">Tier</th>
              <th className="lb-col--peak">Peak</th>
              <th className="lb-col--games">Games</th>
              <th className="lb-col--winrate">Win Rate</th>
              <th>Movement</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <EloRow
                key={row.id}
                row={row}
                rank={idx + 1}
                isSelf={!!currentUser && row.id === currentUser.uid}
                isExpanded={expandedUid === row.id}
                canExpand={!!currentUser && row.id === currentUser.uid}
                onToggleExpand={() => handleToggleExpand(row.id)}
                history={historyByUid[row.id]}
              />
            ))}
            {ownRow && (
              <React.Fragment>
                <tr className="lb-separator"><td colSpan={8}>YOUR POSITION</td></tr>
                <EloRow
                  row={ownRow}
                  rank={null}
                  isSelf
                  isExpanded={expandedUid === ownRow.id}
                  canExpand
                  onToggleExpand={() => handleToggleExpand(ownRow.id)}
                  history={historyByUid[ownRow.id]}
                />
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Stats tab ───────────────────────────────────────────────────────────────

function StatCard({ title, leader, leaderValueFn, you, comingSoon }) {
  if (comingSoon || !leader) {
    return (
      <div className="lb-stat-card lb-stat-card--empty">
        <div className="lb-stat-card__title">{title}</div>
        <div className="lb-stat-card__leader">
          <span className="lb-stat-card__leader-name">—</span>
          <span className="lb-stat-card__coming-soon">Coming soon</span>
        </div>
      </div>
    );
  }
  return (
    <div className="lb-stat-card">
      <div className="lb-stat-card__title">{title}</div>
      <div className="lb-stat-card__leader">
        <span className="lb-stat-card__leader-name">🏆 {getPublicName(leader, null)}</span>
        <span className="lb-stat-card__leader-value">{leaderValueFn(leader)}</span>
      </div>
      <div className="lb-stat-card__you">
        <span className="lb-stat-card__you-label">Your record</span>
        <span className="lb-stat-card__you-value">{you ?? '—'}</span>
      </div>
    </div>
  );
}

function StatsTab({ ownProfile }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [eloSnap, peakSnap, gamesSnap] = await Promise.all([
          getDocs(query(collection(db, 'player_profiles'), orderBy('eloRating', 'desc'), limit(1))),
          getDocs(query(collection(db, 'player_profiles'), orderBy('eloPeak', 'desc'), limit(1))),
          getDocs(query(collection(db, 'player_profiles'), orderBy('eloGamesPlayed', 'desc'), limit(1))),
        ]);
        if (cancelled) return;
        setRecords({
          highestElo:  eloSnap.docs[0]   ? { id: eloSnap.docs[0].id,   ...eloSnap.docs[0].data() }   : null,
          highestPeak: peakSnap.docs[0]  ? { id: peakSnap.docs[0].id,  ...peakSnap.docs[0].data() }  : null,
          mostGames:   gamesSnap.docs[0] ? { id: gamesSnap.docs[0].id, ...gamesSnap.docs[0].data() } : null,
        });
      } catch (e) {
        console.error('[StatsTab] records load error:', e.code, e.message, e);
        if (!cancelled) setError('Something went wrong loading records. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [retryKey]);

  if (loading) return <div className="lb-card"><TableSkeleton rows={4} /></div>;
  if (error) return <div className="lb-card"><ErrorState message={error} onRetry={() => setRetryKey(k => k + 1)} /></div>;

  const noRecordsYet = !records?.highestElo && !records?.highestPeak && !records?.mostGames;
  if (noRecordsYet) {
    return <div className="lb-card"><EmptyState icon="🏆" message="Records will be tracked as more showdowns are completed." /></div>;
  }

  return (
    <div className="lb-stat-grid">
      <StatCard
        title="Highest Current ELO"
        leader={records.highestElo}
        leaderValueFn={p => fmtNum(p.eloRating)}
        you={ownProfile ? fmtNum(ownProfile.eloRating) : null}
      />
      <StatCard
        title="Highest Peak ELO"
        leader={records.highestPeak}
        leaderValueFn={p => fmtNum(p.eloPeak)}
        you={ownProfile ? fmtNum(ownProfile.eloPeak) : null}
      />
      <StatCard
        title="Most Games Played"
        leader={records.mostGames}
        leaderValueFn={p => `${fmtNum(p.eloGamesPlayed)} games`}
        you={ownProfile ? `${fmtNum(ownProfile.eloGamesPlayed)} games` : null}
      />
      <StatCard title="Most Games Won" comingSoon />
      <StatCard title="Most Top 10 Finishes" comingSoon />
      <StatCard title="Most Total Earnings" comingSoon />
      <StatCard title="Highest Single Game Score" comingSoon />
      <StatCard title="Longest Winning Streak" comingSoon />
      <StatCard title="Current Hot Streak" comingSoon />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'showdowns', label: '🏇 Showdowns' },
  { key: 'elo',       label: '📊 ELO Rankings' },
  { key: 'stats',     label: '🏆 Stats' },
];

export default function LeaderboardsPage() {
  const [activeTab, setActiveTab] = useState('showdowns');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedSlateId, setSelectedSlateId] = useState(null);
  const [ownProfile, setOwnProfile] = useState(null);
  const [ownTotalEarnings, setOwnTotalEarnings] = useState(null);
  const [ownRankInSelectedGame, setOwnRankInSelectedGame] = useState(null);

  useEffect(() => onAuthStateChanged(auth, setCurrentUser), []);

  // Own Elo profile — prefer the public player_profiles mirror (same data
  // everyone else sees); fall back to the private users/{uid} doc (always
  // readable by its owner) in case the mirror hasn't landed yet.
  useEffect(() => {
    if (!currentUser) { setOwnProfile(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const profSnap = await getDoc(doc(db, 'player_profiles', currentUser.uid));
        if (cancelled) return;
        if (profSnap.exists() && profSnap.data().eloGamesPlayed) {
          setOwnProfile(profSnap.data());
          return;
        }
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!cancelled && userSnap.exists()) setOwnProfile(userSnap.data());
      } catch (e) {
        console.error('[LeaderboardsPage] own profile load error:', e.code, e.message, e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Lifetime earnings — sum of financialAudit.totalPrizeDividendsWon across
  // every player_entries doc owned by the current user (self-filtered
  // query, allowed under Firestore rules even though a global query is
  // not). This is the SAME field settleRaceResult.js actually increments on
  // settlement and the live game_slates/{slateId}/leaderboard subcollection
  // is built from — totalVirtualEarnings is written once at submission
  // (always 0) and never updated again, so it must never be used here.
  useEffect(() => {
    if (!currentUser) { setOwnTotalEarnings(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'player_entries'),
          where('userId', '==', currentUser.uid)
        ));
        if (!cancelled) {
          const total = snap.docs.reduce((sum, d) => sum + getEntryEarnings(d.data()), 0);
          setOwnTotalEarnings(total);
        }
      } catch (e) {
        console.error('[LeaderboardsPage] total earnings load error:', e.code, e.message, e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Own position in whichever game is currently selected on the Showdowns tab.
  useEffect(() => {
    if (!currentUser || !selectedSlateId) { setOwnRankInSelectedGame(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'game_slates', selectedSlateId, 'leaderboard', currentUser.uid));
        if (!cancelled) setOwnRankInSelectedGame(snap.exists() ? snap.data().position : null);
      } catch (e) {
        console.error('[LeaderboardsPage] own rank load error:', e.code, e.message, e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, selectedSlateId]);

  const tier = eloTier(ownProfile?.eloRating);

  return (
    <div className="dashboard-container">
      <div className="lb-page">
        <div className="lb-header">
          <h1 className="lb-title">Leaderboards</h1>
          <p className="lb-subtitle">Compete for rank, reputation and season prizes.</p>
        </div>

        <div className="lb-layout">
          <div className="lb-main">
            <div className="lb-mobile-stats">
              <YourRankingSidebar
                compact
                profile={ownProfile}
                tier={tier}
                totalEarnings={ownTotalEarnings}
                rank={ownRankInSelectedGame}
              />
            </div>

            <div className="lb-tabs">
              {TABS.map(t => (
                <button
                  key={t.key}
                  className={`lb-tab${activeTab === t.key ? ' lb-tab--active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                  type="button"
                >
                  {t.label}
                </button>
              ))}
            </div>

            {activeTab === 'showdowns' && (
              <ShowdownsTab
                currentUser={currentUser}
                selectedSlateId={selectedSlateId}
                onSelectSlate={setSelectedSlateId}
              />
            )}
            {activeTab === 'elo' && <EloTab currentUser={currentUser} />}
            {activeTab === 'stats' && <StatsTab ownProfile={ownProfile} />}
          </div>

          <div className="lb-sidebar-wrap">
            <YourRankingSidebar
              profile={ownProfile}
              tier={tier}
              totalEarnings={ownTotalEarnings}
              rank={ownRankInSelectedGame}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
